import React, { useState } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../ui/dialog";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import ChoicesTable from "./ChoicesTable";
import { 
  InfoIcon,
  PlusIcon,
  Loader2Icon
} from "lucide-react";

interface AIReviewPayload {
  correction_type: "freeform" | "choices";
  choices?: string[];
}

interface AIReviewProps {
  isVisible: boolean;
  onClose: () => void;
  onStartReview: (payload: AIReviewPayload) => void;
  loading: boolean;
}

const AIReview: React.FC<AIReviewProps> = ({ isVisible, onClose, onStartReview, loading }) => {
  const [correctionType, setCorrectionType] = useState<"freeform" | "choices">("freeform");
  const [choices, setChoices] = useState<string[]>([""]);
  const [savedChoices, setSavedChoices] = useState<string[]>([""]);

  const handleStartReview = () => {
    const payload: AIReviewPayload = {
      correction_type: correctionType,
      ...(correctionType === "choices" && { choices: choices.filter(c => c.trim()) })
    };
    onStartReview(payload);
  };

  const handleCorrectionTypeChange = (value: "freeform" | "choices") => {
    setCorrectionType(value);
    if (value === "choices") {
      // Restore saved choices when switching back to multiple choice
      setChoices(savedChoices);
    } else {
      // Save current choices when switching away from multiple choice
      setSavedChoices(choices);
    }
  };

  const handleChoiceChange = (index: number, value: string) => {
    const newChoices = [...choices];
    newChoices[index] = value;
    setChoices(newChoices);
    setSavedChoices(newChoices);
  };

  const addChoice = () => {
    const newChoices = [...choices, ""];
    setChoices(newChoices);
    setSavedChoices(newChoices);
  };

  const removeChoice = (index: number) => {
    const newChoices = choices.filter((_, i) => i !== index);
    setChoices(newChoices);
    setSavedChoices(newChoices);
  };

  const canStartReview = correctionType === "freeform" || 
    (correctionType === "choices" && choices.filter(c => c.trim()).length >= 2);

  return (
    <Dialog open={isVisible} onOpenChange={!loading ? onClose : undefined}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-background border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-gc-med text-foreground">
            <InfoIcon className="w-5 h-5 text-maitai-green" />
            AI Review Options
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Introduction */}
          <div className="bg-card/50 border border-border rounded-lg p-6">
            <p className="text-sm font-gc-reg text-foreground leading-relaxed">
              An AI Review uses machine learning to analyze your dataset responses and suggest corrections. 
              This helps ensure consistency and accuracy across your training data. You can choose between 
              freeform corrections or predefined options below.
            </p>
          </div>

          {/* Correction Type Selection */}
          <Card className="border-border">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                <InfoIcon className="w-4 h-4 text-maitai-green" />
                Correction Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={correctionType}
                onValueChange={handleCorrectionTypeChange}
                className="space-y-4"
              >
                <div className="flex items-start space-x-3">
                  <RadioGroupItem value="freeform" id="freeform" />
                  <div className="space-y-1">
                    <Label htmlFor="freeform" className="text-sm font-gc-med text-foreground cursor-pointer">
                      Freeform
                    </Label>
                    <p className="text-xs text-muted-foreground font-gc-reg">
                      Allow any text as a correction
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <RadioGroupItem value="choices" id="choices" />
                  <div className="space-y-1">
                    <Label htmlFor="choices" className="text-sm font-gc-med text-foreground cursor-pointer">
                      Multiple Choice
                    </Label>
                    <p className="text-xs text-muted-foreground font-gc-reg">
                      Define specific correction options
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Correction Options */}
          {correctionType === "choices" && (
            <Card className="border-border">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg font-gc-med text-foreground">
                    <InfoIcon className="w-4 h-4 text-maitai-green" />
                    Correction Options
                  </CardTitle>
                  <Button
                    onClick={addChoice}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Choice
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ChoicesTable
                  choices={choices}
                  onChoiceChange={handleChoiceChange}
                  onRemoveChoice={removeChoice}
                />
              </CardContent>
            </Card>
          )}

          {/* Requirements Notice */}
          {correctionType === "choices" && choices.filter(c => c.trim()).length < 2 && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
              <p className="text-sm font-gc-reg text-orange-800 dark:text-orange-200">
                At least two choices are required for multiple choice review.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStartReview}
            disabled={loading || !canStartReview}
            className="gap-2"
          >
            {loading && <Loader2Icon className="w-4 h-4 animate-spin" />}
            Start Review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AIReview; 